import React, { useState } from 'react';
import axios, { type AxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format, formatDistanceToNow } from 'date-fns';
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Eye,
  KeyRound,
  Loader2,
  MoreHorizontal,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { config } from '../../config';
import { useAuth } from '../../hooks/useAuth';
import { useConfirm } from '../../hooks/useConfirm';
import { useRegions } from '../../hooks/useRegions';
import { useDivisions } from '../../hooks/useDivisions';
import { CREDENTIAL_TYPE_LABELS, useCredentials } from '../../hooks/useCredentials';
import CredentialCreateDialog from '../../components/CredentialCreateDialog';
import { CredentialMaterialDialog } from '../../components/CredentialMaterialDialog';
import ConfirmationModal from '../../components/ConfirmationModal';
import ResourceTags from '../../components/ResourceTags';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmptyState } from '@/components/ui/empty-state';
import { PageContainer } from '@/components/ui/page-container';
import ConfigCredentialsSection from '@/components/ConfigCredentialsSection';
import { PageHeader } from '@/components/ui/page-header';
import { cn } from '@/lib/utils';

interface GatewayDeployment {
  gatewayId: string;
  gatewayName: string | null;
  gatewayStatus: string;
  deploymentStatus: string;
  deployedAt: string | null;
  failureReason: string | null;
}

interface Credential {
  id: string;
  name: string;
  type: 'usernamePassword' | 'sshKey' | 'snmpV2Community' | 'snmpV3' | 'x509Cert' | 'apiKey' | 'preSharedKey';
  description: string | null;
  organizationId: string;
  divisionId: string | null;
  regionId: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  username?: string;
  password?: string;
  key?: string;
  community?: string;
}

interface CredentialDetailResponse {
  id: string;
  name: string;
  type: 'usernamePassword' | 'sshKey' | 'snmpV2Community' | 'snmpV3' | 'x509Cert' | 'apiKey' | 'preSharedKey';
  description: string | null;
  organizationId: string;
  divisionId: string | null;
  regionId: string;
  status: string;
  gatewayDeployments: GatewayDeployment[];
  createdAt: string;
  updatedAt: string;
  username?: string;
  key?: string;
  community?: string;
}

interface ApiErrorResponse {
  error: string;
  description?: string;
  /** `credentialReferenced`: stored configs use it; `description` says what deleting costs. */
  code?: string;
}

const backendApi = config.backendApi;

const DEPLOYMENT_STYLE: Record<string, string> = {
  deployed: 'bg-sev-4/15 text-sev-4',
  pending: 'bg-sev-3/15 text-foreground',
  failed: 'bg-sev-1/15 text-sev-1',
  missing: 'bg-sev-3/15 text-foreground',
  not_deployed: 'bg-muted text-muted-foreground',
};

function HealthBadge({ status }: { status: string }) {
  const isHealthy = status === 'healthy';
  return (
    <Badge
      className={cn(
        'border-0 font-normal',
        isHealthy ? 'bg-sev-4/15 text-sev-4' : 'bg-sev-1/15 text-sev-1',
      )}
    >
      {isHealthy ? 'Healthy' : 'Unhealthy'}
    </Badge>
  );
}

/** Shows the status the gateway actually reported rather than collapsing
 *  everything non-online into "Offline": a gateway that has never checked in is
 *  `unknown`, and calling that offline asserts an outage nobody has observed. */
function GatewayStatusBadge({ status }: { status: string }) {
  const isOnline = status === 'online';
  return (
    <Badge
      className={cn(
        'border-0 font-normal capitalize',
        isOnline ? 'bg-sev-5/15 text-sev-5' : 'bg-muted text-muted-foreground',
      )}
    >
      {status}
    </Badge>
  );
}

function DeploymentStatusBadge({ status }: { status: string }) {
  const klass = DEPLOYMENT_STYLE[status] ?? 'bg-muted text-muted-foreground';
  const text = status === 'not_deployed' ? 'Not deployed' : status.charAt(0).toUpperCase() + status.slice(1);
  return <Badge className={cn('border-0 font-normal', klass)}>{text}</Badge>;
}

export default function Credentials() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [createOpen, setCreateOpen] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [expandedCredentials, setExpandedCredentials] = useState<Set<string>>(new Set());
  const [detailsModalCredential, setDetailsModalCredential] = useState<Credential | null>(null);
  const [credentialDetails, setCredentialDetails] = useState<
    Map<string, CredentialDetailResponse>
  >(new Map());

  const [deleteConfirmModalActive, setDeleteConfirmModalActive] = useState(false);
  const [credentialToDelete, setCredentialToDelete] = useState<Credential | null>(null);
  const { confirm, confirmDialog } = useConfirm();
  // Republishing material: `restore` when nothing holds it any more (the secret
  // is unrecoverable and must be retyped), `rotate` when it is merely being
  // replaced. One dialog, because the mechanism is identical.
  const [materialTarget, setMaterialTarget] = useState<{ credential: Credential; mode: 'restore' | 'rotate' } | null>(null);

  const { data: credentialsArray, isLoading: isLoadingCredentials, isError: isErrorCredentials } =
    useCredentials(user?.organizationId);
  const { data: divisions, isLoading: isLoadingDivisions, isError: isErrorDivisions } =
    useDivisions(user?.organizationId);
  const { data: regions, isLoading: isLoadingRegions, isError: isErrorRegions } =
    useRegions(user?.organizationId);

  const deleteCredentialMutation = useMutation<
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    void,
    AxiosError<ApiErrorResponse>,
    { id: string; confirmReferenced?: boolean }
  >({
    mutationFn: async ({ id, confirmReferenced }) => {
      await axios.delete(`${backendApi}/api/credentials/${id}`, {
        withCredentials: true,
        params: confirmReferenced ? { confirmReferenced: true } : undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['credentials'] });
      setDeleteConfirmModalActive(false);
      setCredentialToDelete(null);
      setSubmitSuccess('Credential deleted successfully!');
    },
    onError: async (err, { id }) => {
      // Stored configs use it, and only gateways hold its value: deleting it
      // means those configs can't be restored as they are. The backend says
      // how many; ask again before going ahead.
      if (err.response?.status === 409 && err.response.data.code === 'credentialReferenced') {
        setDeleteConfirmModalActive(false);
        setCredentialToDelete(null);
        const deleteAnyway = await confirm({
          title: 'Stored configs use this credential',
          message: err.response.data.description ?? 'Stored device configs use this credential.',
          confirmText: 'Delete anyway',
          isDanger: true,
        });
        if (deleteAnyway) deleteCredentialMutation.mutate({ id, confirmReferenced: true });
        return;
      }
      setSubmitError(
        err.response?.data.description ||
          err.response?.data.error ||
          'Failed to delete credential.',
      );
      setDeleteConfirmModalActive(false);
      setCredentialToDelete(null);
    },
  });

  const toggleCredentialExpansion = async (credentialId: string) => {
    const newExpanded = new Set(expandedCredentials);
    if (expandedCredentials.has(credentialId)) {
      newExpanded.delete(credentialId);
    } else {
      newExpanded.add(credentialId);
      if (!credentialDetails.has(credentialId)) {
        try {
          const response = await axios.get<CredentialDetailResponse>(
            `${backendApi}/api/credentials/${credentialId}`,
            { withCredentials: true },
          );
          setCredentialDetails((prev) => new Map(prev.set(credentialId, response.data)));
        } catch (err) {
          console.error('Error fetching credential details:', err);
          newExpanded.delete(credentialId);
        }
      }
    }
    setExpandedCredentials(newExpanded);
  };

  const openCreate = () => {
    setSubmitError(null);
    setSubmitSuccess(null);
    setCreateOpen(true);
  };

  const handleDeleteCredential = (credential: Credential) => {
    setCredentialToDelete(credential);
    setDeleteConfirmModalActive(true);
    setSubmitError(null);
    setSubmitSuccess(null);
  };

  const handleConfirmDelete = () => {
    if (credentialToDelete) deleteCredentialMutation.mutate({ id: credentialToDelete.id });
  };

  const handleCancelDelete = () => {
    setDeleteConfirmModalActive(false);
    setCredentialToDelete(null);
  };

  if (isLoadingCredentials || isLoadingDivisions || isLoadingRegions) {
    return (
      <PageContainer>
        <p className="text-sm text-muted-foreground">Loading credentials…</p>
      </PageContainer>
    );
  }

  if (isErrorCredentials || isErrorDivisions || isErrorRegions) {
    return (
      <PageContainer>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Error loading data.</AlertDescription>
        </Alert>
      </PageContainer>
    );
  }

  const credentials = credentialsArray ?? [];
  const divisionsArray = divisions || [];
  const regionsArray = regions || [];
  // Seed the dialog's region picker: preselect when there's exactly one region,
  // otherwise let the user choose.
  const preselectRegion = regionsArray.length === 1 ? regionsArray[0].id : '';

  return (
    <PageContainer>
      <PageHeader
        title="Credentials"
        primary={
          <Button onClick={openCreate} className="gap-1.5">
            <Plus className="w-4 h-4" />
            Create credential
          </Button>
        }
      />

      {submitSuccess ? (
        <Alert className="mb-4 border-sev-4/40 bg-sev-4/10 relative">
          <CheckCircle2 className="h-4 w-4 text-sev-4" />
          <AlertDescription>{submitSuccess}</AlertDescription>
          <button
            type="button"
            onClick={() => { setSubmitSuccess(null); }}
            className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </Alert>
      ) : null}

      {submitError ? (
        <Alert variant="destructive" className="mb-4 relative">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{submitError}</AlertDescription>
          <button
            type="button"
            onClick={() => { setSubmitError(null); }}
            className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </Alert>
      ) : null}

      {credentials.length === 0 ? (
        <EmptyState
          variant="no-data"
          icon={KeyRound}
          title="No credentials yet"
          description="Credentials let the gateway authenticate to your devices: SSH keys, SNMP communities, login pairs."
          action={
            <Button onClick={openCreate} className="gap-1.5">
              <Plus className="w-4 h-4" />
              Create credential
            </Button>
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border-subtle">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2 font-medium w-8" />
                <th className="px-3 py-2 font-medium">Name</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Health</th>
                <th className="px-3 py-2 font-medium">Region</th>
                <th className="px-3 py-2 font-medium">Division</th>
                <th className="px-3 py-2 font-medium">Description</th>
                <th className="px-3 py-2 font-medium">Created</th>
                <th className="px-3 py-2 font-medium">Updated</th>
                <th className="px-3 py-2 font-medium w-12" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {credentials.map((cred) => {
                const isExpanded = expandedCredentials.has(cred.id);
                const details = credentialDetails.get(cred.id);
                return (
                  <React.Fragment key={cred.id}>
                    <tr className="hover:bg-accent/30">
                      <td className="px-3 py-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            void toggleCredentialExpansion(cred.id);
                          }}
                          aria-label={isExpanded ? 'Collapse details' : 'Expand details'}
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronRight className="w-3.5 h-3.5" />
                          )}
                        </Button>
                      </td>
                      <td className="px-3 py-2 font-medium">{cred.name}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {CREDENTIAL_TYPE_LABELS[cred.type]}
                      </td>
                      <td className="px-3 py-2"><HealthBadge status={cred.status} /></td>
                      <td className="px-3 py-2 text-xs">
                        {regionsArray.find((r) => r.id === cred.regionId)?.name || cred.regionId}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {divisionsArray.find((d) => d.id === cred.divisionId)?.name || '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {cred.description || '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        <span title={format(new Date(cred.createdAt), 'PPpp')}>
                          {formatDistanceToNow(new Date(cred.createdAt), { addSuffix: true })}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        <span title={format(new Date(cred.updatedAt), 'PPpp')}>
                          {formatDistanceToNow(new Date(cred.updatedAt), { addSuffix: true })}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              aria-label={`Actions for ${cred.name}`}
                            >
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setMaterialTarget({
                                  credential: cred,
                                  // Unhealthy is the closest signal the list
                                  // already has to "nothing holds this"; the
                                  // dialog only uses it for wording, and the
                                  // backend decides what actually happens.
                                  mode: cred.status === 'healthy' ? 'rotate' : 'restore',
                                });
                              }}
                              className="gap-2"
                            >
                              <KeyRound className="w-4 h-4" />
                              {cred.status === 'healthy' ? 'Replace material' : 'Restore material'}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => { handleDeleteCredential(cred); }}
                              className="gap-2 text-sev-1 focus:text-sev-1"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                    {isExpanded ? (
                      <tr>
                        <td colSpan={10} className="bg-muted/30 px-6 py-4">
                          {details ? (
                            <>
                              {details.gatewayDeployments.length > 0 ? (
                                (() => {
                                  const dep = details.gatewayDeployments;
                                  const deployed = dep.filter(
                                    (d) => d.deploymentStatus.toLowerCase() === 'deployed',
                                  ).length;
                                  const pending = dep.filter(
                                    (d) => d.deploymentStatus.toLowerCase() === 'pending',
                                  ).length;
                                  const failed = dep.filter(
                                    (d) => d.deploymentStatus.toLowerCase() === 'failed',
                                  ).length;
                                  return (
                                    <div className="flex items-center justify-between mb-3">
                                      <div>
                                        <p className="text-sm font-semibold mb-1">
                                          Gateway deployments
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                          {dep.length}{' '}
                                          {dep.length === 1 ? 'deployment' : 'deployments'}
                                          {deployed > 0 ? (
                                            <span className="text-sev-4 ml-2">
                                              • {deployed} deployed
                                            </span>
                                          ) : null}
                                          {pending > 0 ? (
                                            <span className="text-sev-3 ml-2">
                                              • {pending} pending
                                            </span>
                                          ) : null}
                                          {failed > 0 ? (
                                            <span className="text-sev-1 ml-2">
                                              • {failed} failed
                                            </span>
                                          ) : null}
                                        </p>
                                      </div>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => { setDetailsModalCredential(cred); }}
                                        className="gap-1.5"
                                      >
                                        <Eye className="w-3.5 h-3.5" />
                                        View details
                                      </Button>
                                    </div>
                                  );
                                })()
                              ) : (
                                <p className="text-xs text-muted-foreground mb-3">
                                  No gateway deployments for this credential.
                                </p>
                              )}
                              <ResourceTags resourceType="credential" resourceId={cred.id} />
                            </>
                          ) : (
                            <p className="text-xs text-muted-foreground flex items-center gap-2">
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              Loading deployment details…
                            </p>
                          )}
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CredentialCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        regionId={preselectRegion}
        regions={regionsArray}
        onCreated={() => {
          void queryClient.invalidateQueries({ queryKey: ['workflow', 'onboarding'] });
          setSubmitSuccess('Credential created successfully!');
        }}
      />

      {materialTarget ? (
        <CredentialMaterialDialog
          credential={materialTarget.credential}
          organizationId={user?.organizationId}
          mode={materialTarget.mode}
          onClose={(replaced) => {
            setMaterialTarget(null);
            if (replaced) {
              setSubmitSuccess(
                materialTarget.mode === 'restore'
                  ? 'Credential material restored. Gateways that were offline will pick it up when they report.'
                  : 'Credential material replaced. Gateways that were offline will pick it up when they report.',
              );
            }
          }}
        />
      ) : null}

      <ConfirmationModal
        isActive={deleteConfirmModalActive}
        title="Delete credential"
        message={`Are you sure you want to delete the credential "${credentialToDelete?.name ?? 'Unknown'}"? This action cannot be undone and will remove the credential from all gateways.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={deleteCredentialMutation.isPending}
        isDanger={true}
      />

      <ConfigCredentialsSection />

      <Dialog
        open={!!detailsModalCredential}
        onOpenChange={(open) => { if (!open) setDetailsModalCredential(null); }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Gateway deployments — {detailsModalCredential?.name ?? ''}
            </DialogTitle>
          </DialogHeader>
          {detailsModalCredential
            ? (() => {
                const details = credentialDetails.get(detailsModalCredential.id);
                if (!details) {
                  return (
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading deployment details…
                    </p>
                  );
                }
                if (details.gatewayDeployments.length === 0) {
                  return (
                    <p className="text-sm text-muted-foreground">
                      No gateway deployments found for this credential.
                    </p>
                  );
                }
                return (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="border-b border-border-subtle">
                        <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                          <th className="px-3 py-2 font-medium">Gateway</th>
                          <th className="px-3 py-2 font-medium">Gateway status</th>
                          <th className="px-3 py-2 font-medium">Deployment status</th>
                          <th className="px-3 py-2 font-medium">Deployed at</th>
                          <th className="px-3 py-2 font-medium">Failure reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border-subtle">
                        {details.gatewayDeployments.map((d) => (
                          <tr key={d.gatewayId}>
                            <td className="px-3 py-2">{d.gatewayName || d.gatewayId}</td>
                            <td className="px-3 py-2">
                              <GatewayStatusBadge status={d.gatewayStatus} />
                            </td>
                            <td className="px-3 py-2">
                              <DeploymentStatusBadge status={d.deploymentStatus} />
                            </td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">
                              {d.deployedAt ? (
                                <span title={format(new Date(d.deployedAt), 'PPpp')}>
                                  {formatDistanceToNow(new Date(d.deployedAt), {
                                    addSuffix: true,
                                  })}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="px-3 py-2 text-xs text-sev-1">
                              {d.failureReason || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()
            : null}
        </DialogContent>
      </Dialog>
      {confirmDialog}
    </PageContainer>
  );
}
